import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { request } from '../../api/client';
import { grantAlive, grantDeadline, type Lease } from './lease';
import { captureEncrypted, newTransferKey, restoreEncrypted } from './handoff';
import { nativeChat, openNativeChat, type Launch, type PairBinding } from './native';

type BrowserBinding={device:string;transferKey:string};
export type PreparedDetach={binding:BrowserBinding;url:string;expiresAt:number};
type Reply={owner:'browser'|'native';target:'browser'|'native';epoch:number;grant_seconds:number;yield_requested:boolean;waiting_for_app:boolean;recovery?:{owner:string;epoch:number;until:number}|null};
type Phase='browser'|'opening'|'native'|'returning';
const emptyLease:Lease={wall:0,monotonic:0};
function read<T>(key:string):T|null{try{return JSON.parse(localStorage.getItem(key)||'null') as T|null;}catch{return null;}}
const api=<T,>(payload:unknown)=>request<T>('/api/chat-companion',{method:'POST',headers:{'X-Skrepka-Companion':'1'},body:payload,signal:AbortSignal.timeout(7000)});

export function useCompanion(userId:number, localPrimary:boolean, workspace:boolean) {
  const native=Boolean(nativeChat());
  const storageKey=`skrepka.chat-companion.${userId}`;
  const [binding,setBinding]=useState<BrowserBinding|PairBinding|null>(()=>native?null:read<BrowserBinding>(storageKey));
  const [phase,setPhase]=useState<Phase>(()=>{
    if(!native&&binding)try{
      const saved=localStorage.getItem(storageKey+'.phase');
      if(saved==='native'||saved==='returning'||saved==='opening')return saved;
    }catch{/* Server polling will restore the authoritative state. */}
    return 'browser';
  });
  const [error,setError]=useState('');
  const [launchError,setLaunchError]=useState('');
  const [notice,setNotice]=useState('');
  const launchDeadline=useRef(0);
  const lastClaim=useRef<{ticket:string;promise:Promise<void>}|null>(null);
  const [granted,setGranted]=useState(false);
  const [restoredSearch,setRestoredSearch]=useState<string|null>(null);
  const ready=useRef(false),busy=useRef(false),busySources=useRef(new Set<string>()),inFlight=useRef(false),lease=useRef(emptyLease),desiredCommand=useRef('poll');
  const epoch=useRef(0), yielding=useRef(false), hadNative=useRef(false), closeWanted=useRef(false);
  const localOwner=useRef(localPrimary);
  const bindingRef=useRef(binding);
  const recoveryCheck=useRef<null|(()=>Promise<boolean>)>(null);
  const registerRecoveryCheck=useCallback((check:()=>Promise<boolean>)=>{
    recoveryCheck.current=check;
    return()=>{if(recoveryCheck.current===check)recoveryCheck.current=null;};
  },[]);
  useLayoutEffect(()=>{localOwner.current=localPrimary;bindingRef.current=binding;},[localPrimary,binding]);
  const isAllowed=useCallback(()=>!workspace && (bindingRef.current ? grantAlive(lease.current,performance.now(),Date.now()) : !native),[native,workspace]);
  const reportReady=useCallback((value:boolean)=>{ready.current=value;},[]);
  const reportBusy=useCallback((source:string,value:boolean)=>{if(value)busySources.current.add(source);else busySources.current.delete(source);busy.current=busySources.current.size>0;void nativeChat()?.setBusy(busy.current);},[]);
  const setStatus=useCallback((value:Phase)=>{
    setPhase(value);
    if(!native)try{localStorage.setItem(storageKey+'.phase',value);}catch{/* Existing notification owner still governs this window. */}
  },[native,storageKey]);
  const payload=useCallback((action:string, extra:Record<string,unknown>={})=>{
    const b=bindingRef.current;
    return {device:b?.device,...(b && 'secret' in b?{secret:b.secret}:{}),action,role:native?'native':'browser',...extra};
  },[native]);
  const claim=useCallback((launch:Launch)=>{
    // Bootstrap and a second-instance event can deliver the same one-use
    // invitation while the renderer is starting. Consume it exactly once.
    if(lastClaim.current?.ticket===launch.ticket)return lastClaim.current.promise;
    const promise=(async()=>{
      if(busy.current)throw Error('Сначала завершите звонок.');
      setError('');setNotice('Подключаем чат из браузера…');
      void nativeChat()?.diagnostic?.('claim-start');
      try{
        const result=await api<Omit<PairBinding,'transferKey'>>({action:'claim',ticket:launch.ticket});
        if(result.userId!==userId)throw Error('Войдите в тот же аккаунт, что в браузере.');
        const next={...result,transferKey:launch.key};
        await nativeChat()?.saveBinding(next);bindingRef.current=next;setBinding(next);setStatus('opening');
        void nativeChat()?.diagnostic?.('claim-success');
      }catch(reason){void nativeChat()?.diagnostic?.('claim-error');setNotice('');throw reason;}
    })();
    lastClaim.current={ticket:launch.ticket,promise};return promise;
  },[setStatus,userId]);
  useEffect(()=>{
    if(!native)return;
    let cancelled=false;
    void nativeChat()!.bootstrap().then(async data=>{
      if(cancelled)return;
      if(data.ticket)await claim(data.ticket);
      else if(data.binding?.userId===userId){bindingRef.current=data.binding;setBinding(data.binding);setStatus('opening');}
      else{setNotice('Откройте приложение правой кнопкой на значке чата в браузере.');void nativeChat()?.diagnostic?.('awaiting-invitation');}
    }).catch(reason=>setError(String(reason instanceof Error?reason.message:reason)));
    const off=nativeChat()!.onLaunch(launch=>void claim(launch).catch(reason=>setError(reason instanceof Error?reason.message:'Не удалось открыть приглашение')));
    return()=>{cancelled=true;off();};
  },[native,userId,claim,setStatus]);
  useEffect(()=>{
    if(native)return;
    const timer=window.setInterval(()=>{
      if(launchDeadline.current && performance.now()>=launchDeadline.current){
        launchDeadline.current=0;setNotice('');setStatus('browser');
        setLaunchError('Приложение не подтвердило подключение. Чат продолжает работать здесь.');
      }
    },500);
    return()=>clearInterval(timer);
  },[native,setStatus]);
  useEffect(()=>{
    if(native)return;
    const changed=(event:StorageEvent)=>{
      if(event.key===storageKey){setBinding(read<BrowserBinding>(storageKey));lease.current=emptyLease;setGranted(false);}
      if(event.key===storageKey+'.phase')setPhase((event.newValue as Phase)||'browser');
    };
    window.addEventListener('storage',changed);return()=>window.removeEventListener('storage',changed);
  },[native,storageKey]);
  const returnToBrowser=useCallback(()=>{
    if(busy.current){setError('Сначала завершите звонок или отправку файла.');return;}
    launchDeadline.current=0;setNotice('');setLaunchError('');
    desiredCommand.current='return';setStatus('returning');
  },[setStatus]);
  useEffect(()=>{
    if(!native)return;
    return nativeChat()!.onClose(()=>{
      if(busy.current){setError('Сначала завершите звонок или отправку файла.');return;}
      if(!bindingRef.current){void nativeChat()!.finishClose();return;}
      closeWanted.current=true;returnToBrowser();
    });
  },[native,returnToBrowser]);
  useEffect(()=>{
    if(!binding || workspace || !localPrimary)return;
    let stopped=false;
    const tick=async()=>{
      if(inFlight.current || stopped)return;
      inFlight.current=true;
      try{
        const started=performance.now();
        const command=desiredCommand.current;
        const result=await api<Reply>(payload(command,{ready:ready.current,busy:busy.current,yielded:yielding.current}));
        if(stopped)return;
        if(desiredCommand.current===command)desiredCommand.current='poll';
        if(result.recovery && !busy.current && ready.current && recoveryCheck.current){
          const idle=await recoveryCheck.current();
          if(stopped)return;
          if(idle && !busy.current && ready.current){
            await api(payload('recover-idle',{observed:result.recovery,ready:true,busy:false}));
            return; // Receive a fresh lease in the next poll, after reconciliation.
          }
        }
        if(result.yield_requested && !busy.current){
          // Stop notifications synchronously before acknowledging release.
          lease.current=emptyLease;setGranted(false);
          if(!yielding.current){
            const cipher=await captureEncrypted(userId,binding.transferKey);
            await api(payload('payload',{cipher}));
            yielding.current=true;
          }
          setStatus(native?'returning':'opening');
          return;
        }
        if(result.grant_seconds>0){
          lease.current=grantDeadline(result.grant_seconds,started,performance.now(),Date.now());
          yielding.current=false;
          if(epoch.current!==result.epoch){
            const data=await api<{cipher:string|null}>(payload('read-payload'));
            if(stopped)return;
            if(data.cipher)setRestoredSearch(await restoreEncrypted(userId,binding.transferKey,data.cipher));
            epoch.current=result.epoch;
          }
          setGranted(isAllowed());
        }else{lease.current=emptyLease;setGranted(false);}
        if(result.owner==='native'){launchDeadline.current=0;setLaunchError('');setNotice('');}
        if(result.owner==='native'){hadNative.current=true;setStatus('native');}
        else if(result.target==='native')setStatus('opening');
        else{
          setStatus(launchDeadline.current?'opening':'browser');
          if(native && (hadNative.current||closeWanted.current)){
            await nativeChat()!.saveBinding(null);await nativeChat()!.finishClose();
          }
          // Once return is acknowledged, normal browser leadership is restored.
          if(!native && result.grant_seconds>0 && (hadNative.current||command==='return')){
            // This return completes one pairing. Its history must not clear the
            // next pairing while the app is still claiming its new invitation.
            hadNative.current=false;yielding.current=false;epoch.current=0;
            localStorage.removeItem(storageKey);bindingRef.current=null;setBinding(null);
          }
        }
        if(!stopped)setError('');
      }catch(reason){if(!stopped)setError(reason instanceof Error?reason.message:'Не удалось передать управление чатом');}
      finally{inFlight.current=false;}
    };
    void tick();const timer=window.setInterval(()=>void tick(),2500);
    const expiry=window.setInterval(()=>{if(!isAllowed())setGranted(false);},500);
    return()=>{stopped=true;clearInterval(timer);clearInterval(expiry);lease.current=emptyLease;};
  },[binding,workspace,localPrimary,payload,userId,native,isAllowed,setStatus,storageKey]);
  const prepareDetach=useCallback(async():Promise<PreparedDetach>=>{
    if(native||workspace)throw Error('Откройте чат в основной вкладке Скрепки.');
    if(!localOwner.current)throw Error('Откройте чат в главной вкладке Скрепки.');
    if(busy.current)throw Error('Сначала завершите звонок или отправку файла.');
    const next=bindingRef.current??{device:crypto.randomUUID(),transferKey:newTransferKey()};
    const started=Date.now();
    const reply=await api<{ticket:string;expires_in:number}>({device:next.device,role:'browser',action:'ticket',busy:false});
    return {binding:next,expiresAt:started+Math.min(reply.expires_in,120)*1000,
      url:`skrepka-chat://open?ticket=${encodeURIComponent(reply.ticket)}&key=${encodeURIComponent(next.transferKey)}`};
  },[native,workspace]);
  const detach=useCallback((prepared:PreparedDetach)=>{
    if(native||workspace)return;
    if(!localOwner.current){setError('Откройте чат в главной вкладке Скрепки.');return;}
    if(busy.current){setError('Сначала завершите звонок или отправку файла.');return;}
    if(Date.now()>=prepared.expiresAt){setError('Приглашение истекло. Откройте меню чата ещё раз.');return;}
    setError('');
    setLaunchError('');launchDeadline.current=performance.now()+30000;
    setNotice('Открываем «Скрепка Чат»… Если браузер запросит разрешение, подтвердите открытие приложения.');
    try{
      const next=prepared.binding;
      // Key never reaches the server. Only encrypted transient view state does.
      localStorage.setItem(storageKey,JSON.stringify(next));bindingRef.current=next;setBinding(next);setStatus('opening');
      // Keep protocol navigation in the actual click, with no asynchronous gap.
      openNativeChat(prepared.url);
    }catch(reason){launchDeadline.current=0;setNotice('');setLaunchError(reason instanceof Error?reason.message:'Не удалось открыть приложение');}
  },[native,workspace,storageKey,setStatus]);
  return {native,phase,error:launchError||error,launchFailed:Boolean(launchError),notice,prepareDetach,detach,returnToBrowser,reportReady,reportBusy,registerRecoveryCheck,isAllowed,
    allowed:!workspace && (binding?granted:!native),restoredSearch,
    clearRestoredSearch:()=>setRestoredSearch(null)};
}
